import type { SyntheticEvent } from 'react';
import {
  forwardRef,
  use,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useAnimation } from 'framer-motion';
import { partition } from 'lodash';
import styled from 'styled-components';
import type {
  AuthzNamespaceEnum,
  CreateMembershipsInput,
  Membership,
  Resource,
  SuggestedAccountUser,
  SuggestedAccountUserGroup,
  SuggestedShareList,
} from '@frameio/graphql-client/generated';
import {
  GetDirectMemberships,
  GetDirectMembershipsForFacepile,
  GetSuggestedMembers,
  useGetAccountUsageQuery,
  useGetResourceQuery,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import {
  useFolderPermissions,
  useProjectPermissions,
  useWorkspacePermissions,
} from '@frameio/permissions';
import { useAccountSubscriptionDetails } from '@frameio/plan-management/plans/hooks/useAccountSubscriptionDetails';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { Emoji } from '@frameio/shared/emoji';
import { useAccountManagedByAdobe } from '@frameio/shared/hooks';
import { UpdateAppParams } from '@frameio/shared/routing/params/params';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { DEFAULT_INITIAL_PAGE_SIZE, imageLoader } from '@frameio/utils';
import { isValidEmail } from '@frameio/utils/validators';
import type { ThemeColorsBackground, ThemeColorsMeta } from '@frameio/vapor';
import {
  AccordionDialog,
  AccordionDialogItem,
  Avatar,
  AvatarGroup,
  ProjectAvatar,
  Skeleton,
  theme,
  useDeferredMount,
} from '@frameio/vapor';
import { useCreateMemberships } from '../../hooks/useCreateMemberships';
import { useGetDirectMemberships } from '../../hooks/useGetDirectMemberships';
import { useIsRestrictedFolder } from '../../hooks/useIsRestrictedFolder';
import { usePreloadAccessInfo } from '../../hooks/usePreloadAccessInfo';
import type { InputToken } from '../../utils';
import {
  getResourceName,
  getRoleNameForTracking,
  InputTokenStatus,
  INVITE_MESSAGE_MAX_LENGTH,
  useReadMembershipFragment,
} from '../../utils';
import { ExistingGuestDialog } from '../ExistingGuestDialog';
import { InviteAccessMembers } from '../InviteAccessMembers';
import {
  MemberAccessForm,
  useSuggestedMemberSearch,
} from '../MemberAccessForm';
import { ResourceIcon } from '../ResourceIcon';
import TEXT from './generated/TEXT/MemberAccessDialog';
import { getMemberSeatLimitState } from './getMemberSeatLimitState';
import { useLicenseSelect } from './hooks/useLicenseSelect';
import { useMemberAccessRoleSelect } from './hooks/useMemberAccessRoleSelect';
import { useMemberEmailInputQuery } from './hooks/useMemberEmailInputQuery';
import { useMemberInputToken } from './hooks/useMemberInputToken';
import styles from './MemberAccessDialog.module.css';

TEXT.DEFINE({
  /** And alt text for an image that displays an emoji */
  emoji: 'Emoji',
  /** Text for the button that adds members to a resource in the member access dialog */
  labelInviteButton: 'Add',
  /** Text for header of one of the accordion dialog items that says to which resource to add members in the member access dialog */
  labelInvite: (name?: string, resourceType?: string) => {
    if (resourceType === 'FolderAsset') {
      return `Add to Folder "${name}"`;
    }
    return `Add to ${name}`;
  },
  /** Text for the accordion dialog item that manages members in the member access dialog */
  labelManage: 'Manage',
  /** Text for the header of one of the accordion dialog items that displays the number of members in the member access dialog */
  labelMembers: (count: number) =>
    plural(count, {
      one: `${count} Member`,
      other: `${count} Members`,
    }),
  /** Aria label text for the accordion dialog item that manages members in the member access dialog */
  ariaLabelManageMembers: 'Manage these members',
});

const LIST_TRANSITION_TIMING = 0.15;
const MEMBER_TYPES = {
  MEMBER: 'Member',
  GUEST: 'Guest',
};

/**
 * Provides the useAnimation controls and animation callbacks for use with
 * InviteAccessMembers
 */
function useListAnimationControls() {
  const memberListControls = useAnimation();
  const animateMemberListOut = useCallback(async () => {
    await memberListControls.start({
      opacity: 0,
      y: theme.spaces[24],
      transition: {
        duration: LIST_TRANSITION_TIMING,
        ease: theme.transitions.curves.primary,
      },
    });

    return memberListControls.stop();
  }, [memberListControls]);

  const animateMemberListIn = useCallback(async () => {
    memberListControls.set({
      opacity: 0,
      y: `calc(${theme.spaces[12]} * -1)`,
    });

    await memberListControls.start({
      opacity: 1,
      y: 0,
      transition: {
        delay: LIST_TRANSITION_TIMING * 1.5,
        duration: LIST_TRANSITION_TIMING,
        ease: theme.transitions.curves.primary,
      },
    });

    return memberListControls.stop();
  }, [memberListControls]);

  return { animateMemberListIn, animateMemberListOut, memberListControls };
}

export type MemberAccessDialogProps = {
  accountId: string;
  canViewMembers?: boolean;
  hideSuggestedMembers?: boolean;
  isDialogShowing?: boolean;
  onRequestChange?: (shouldShow: boolean) => void;
  onResourceHasMutated?: (
    isCurrentResourceNotOriginalContext?: boolean
  ) => void;
  prefetchData?: boolean;
  resourceId?: Resource['id'];
  showMembersInitially?: boolean;
  trackingSource?: TrackingSourceType;
};

const ResourceAvatar = ({
  resourceImage,
  resourceType,
  resourcePrivacy,
  resourceName,
  resourceId,
}: {
  resourceType: string;
  resourceImage?: string;
  resourcePrivacy?: boolean;
  resourceName?: string;
  resourceId?: string;
}) => {
  const renderAvatar = () => {
    if (resourceType === 'Project') {
      return (
        <ProjectAvatar
          id={resourceId}
          isPrivate={resourcePrivacy}
          size={24}
          src={resourceImage}
          title={resourceName}
        />
      );
    }

    if (resourceImage) {
      return <Avatar aria-hidden="true" shape="square" src={resourceImage} />;
    }

    return (
      <ResourceIcon id={resourceId} type={resourceTypeToEnum(resourceType)} />
    );
  };

  return (
    <div
      className={
        resourceImage
          ? styles.resourceAvatarWrapperWithResourceImage
          : styles.renderAvatarWrapper
      }
    >
      {renderAvatar()}
    </div>
  );
};

const MemberAvatar = ({ membershipId }: { membershipId: Membership['id'] }) => {
  const { member } = useReadMembershipFragment(membershipId);

  if (member?.accountUser) {
    return (
      <Avatar
        alt={member.accountUser.displayName}
        background={
          member.accountUser.avatarColor as
            | ThemeColorsBackground
            | ThemeColorsMeta
        }
        src={
          member.accountUser?.profileImageUrl &&
          imageLoader({
            src: member.accountUser?.profileImageUrl,
            width: 64,
          })
        }
      />
    );
  }

  if (member?.accountUserGroup) {
    if (!member?.accountUserGroup?.emoji) {
      return null;
    }

    return (
      <Avatar
        alt={`${member.accountUserGroup.emoji} ${TEXT.emoji}`}
        background="primary"
        emojiComponent={
          <Emoji emoji={member.accountUserGroup.emoji} size={13} />
        }
        shape="circle"
        size={24}
      />
    );
  }

  return null;
};

function resourceTypeToEnum(resourceType?: string): AuthzNamespaceEnum | null {
  switch (resourceType) {
    case 'Account':
      return 'ACCOUNT';
    case 'Workspace':
      return 'WORKSPACE';
    case 'Project':
      return 'PROJECT';
    case 'FolderAsset':
    case 'FOLDER':
      return 'FOLDER';
    default:
      return null;
  }
}

export const MemberAccessDialog = forwardRef(
  (
    {
      accountId,
      canViewMembers = true,
      hideSuggestedMembers = false,
      isDialogShowing,
      onRequestChange,
      onResourceHasMutated,
      prefetchData = false,
      resourceId,
      showMembersInitially,
      trackingSource,
    }: MemberAccessDialogProps,
    forwardedRef
  ) => {
    const [isExistingGuestDialogShowing, setIsExistingGuestDialogShowing] =
      useState(false);
    const [fetchData, setFetchData] = useState(prefetchData);
    const [isShowing, setIsShowing] = useState(isDialogShowing);
    const [currentResourceId, setCurrentResourceId] = useState(resourceId);
    const shouldRender = useDeferredMount();
    const initialFocusRefForInvite = useRef<HTMLInputElement>(undefined);
    const activeProjectId = useActiveProjectId();

    const { animateMemberListIn, animateMemberListOut, memberListControls } =
      useListAnimationControls();

    const { data, previousData } = useGetResourceQuery({
      variables: { accountId, id: currentResourceId },
      skip: !currentResourceId || !accountId || (!fetchData && !isShowing),
    });

    useEffect(() => {
      if (!data) return;
      /**
       * If the resource has changed, we can safely assume the list has been
       * animated out. Animate back in once data has finished loading.
       */
      if (data.account?.resource?.id !== resourceId) {
        animateMemberListIn();
      }
      // Only fire when a new completed result arrives (mirrors `onCompleted`).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const resource = (data ?? previousData)?.account?.resource;
    const resourceType = resource?.__typename;
    const resourceName = getResourceName(resource);
    const resourcePrivacy =
      resourceType === 'Project' ? resource?.private : false;

    const { data: accountUsage } = useGetAccountUsageQuery({
      variables: { accountId },
      skip: !accountId,
    });

    const { subscriptionCost, planPeriod } = useAccountSubscriptionDetails();

    const subscription = accountUsage?.account?.subscription;
    const guestSeatsIncluded =
      (accountUsage?.account?.usageByLicenseType?.guest?.seatLimit ?? 0) > 0;
    const isOnTrial = subscription?.trial?.isActive ?? false;
    const isFreeTierPlan = subscription?.plan?.tier === 'FREE';

    const canInviteGuests =
      guestSeatsIncluded && resource?.__typename === 'Project';

    const guestSeatUsage = accountUsage?.account?.usageByLicenseType?.guest;
    const memberSeatUsage = accountUsage?.account?.usageByLicenseType?.member;

    let resourceImage = '';
    if (resource?.__typename === 'Project') {
      resourceImage = resource?.projectImages.image64 ?? '';
    } else if (resource?.__typename === 'Workspace') {
      resourceImage = resource?.workspaceImages?.image64 ?? '';
    }

    const { availableRoles, currentRoleId, onChangeRole } =
      useMemberAccessRoleSelect({
        resourceId: currentResourceId ?? '',
        enabled: Boolean(currentResourceId) && (isShowing || fetchData),
      });

    const { allMembers, matchedMembers, onQueryChange, isLoading } =
      useSuggestedMemberSearch({
        count: 50,
        resourceId,
        prefetch:
          Boolean(currentResourceId) &&
          shouldRender &&
          (fetchData || isShowing),
        skip: hideSuggestedMembers,
      });

    const refetchQueries = useMemo(() => {
      const queries = [];

      if (resourceId !== currentResourceId) {
        queries.push({
          query: GetDirectMemberships,
          variables: {
            accountId,
            resourceId,
            page: {
              first: DEFAULT_INITIAL_PAGE_SIZE,
            },
          },
        });
      }

      if (resourceType === 'FolderAsset') {
        queries.push({
          query: GetSuggestedMembers,
          variables: {
            filter: {
              query: '',
            },
            resourceId,
            orderBy: {
              field: 'NAME',
              direction: 'ASC_NULLS_LAST',
            },
            page: {
              first: DEFAULT_INITIAL_PAGE_SIZE,
            },
          },
        });

        queries.push({
          query: GetDirectMembershipsForFacepile,
          variables: {
            accountId,
            resourceId,
          },
        });
      }

      return queries.length ? queries : undefined;
    }, [accountId, resourceId, currentResourceId, resourceType]);

    const createMemberships = useCreateMemberships({
      resourceId: currentResourceId,
      resourceType,
      refetchQueries,
    });

    const handleCreateMemberships = async ({
      roleId,
      members,
      licenseType,
      message,
      notify,
    }: Pick<
      CreateMembershipsInput,
      'roleId' | 'licenseType' | 'message' | 'notify' | 'members'
    >) => {
      const response = await createMemberships({
        licenseType,
        roleId,
        members,
        message,
        notify,
      });

      onResourceHasMutated?.();
      return response;
    };

    useEffect(() => {
      setIsShowing(isDialogShowing);
      let resetDialogTimeout;

      /**
       * If the dialog is closed, reset the current resource context. The timeout
       * prevents a flash of seeing the new content before the animation out
       * completes
       */
      if (!isDialogShowing) {
        resetDialogTimeout = setTimeout(() => {
          setCurrentResourceId(resourceId);
        }, 200);
      }

      return () => resetDialogTimeout && clearTimeout(resetDialogTimeout);
    }, [isDialogShowing, resourceId]);

    /**
     * This effect accounts for a potential edge case where the route may change
     * to a different resource but the dialog lives in a part of the app that
     * does not unmount when the route changes. We want the dialog to close
     * when the resource is changed without it being initiated by the dialog
     * itself.
     */
    useEffect(() => {
      setCurrentResourceId(resourceId);
    }, [resourceId]);

    const handleChangeResource = async (id: string) => {
      await animateMemberListOut();
      setCurrentResourceId(id);
      initialFocusRefForMemberList.current?.focus();
    };

    const { directMemberships, uniqueMemberCount } = useGetDirectMemberships({
      fetchData: (fetchData || isDialogShowing) && Boolean(currentResourceId),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      resourceId: currentResourceId,
    });

    const directMembershipCount = directMemberships?.totalCount ?? 0;

    const firstThreeMemberIds = useMemo(() => {
      return directMemberships?.nodes.slice(0, 3).map((member) => member.id);
    }, [directMemberships]);

    const canManageWorkspaceAccess = useWorkspacePermissions({
      check: 'canEditUserPermissions',
      workspaceId: currentResourceId,
    });

    const canManageProjectAccess = useProjectPermissions({
      check: 'canEditUserPermissions',
      projectId: currentResourceId ?? activeProjectId,
    });

    const canManageFolderAccess = useFolderPermissions({
      check: 'canEditUserPermissions',
      /**
       * @note: we're checking the permission of the target folder, instead of
       * checking its parent folder's permissions. this is because the user might
       * have View Only access to this folder, but they might have Full Access to
       * the parent. the user shouldn't be able to change their own access level
       * in that case, even though the permission structure would technically allow
       * them to.
       */
      folderId:
        resource?.__typename === 'FolderAsset' ? resource.id : undefined,
    });

    const accessCanBeManaged =
      (data?.account?.resource?.__typename === 'Workspace' &&
        canManageWorkspaceAccess) ||
      (data?.account?.resource?.__typename === 'Project' &&
        canManageProjectAccess) ||
      (data?.account?.resource?.__typename === 'FolderAsset' &&
        canManageFolderAccess);

    const isFolderAsset = resourceType === 'FolderAsset';

    const isRestrictedFolder = useIsRestrictedFolder(
      currentResourceId,
      !isFolderAsset
    );
    const isRestrictedProject =
      resourceType === 'Project' && Boolean(resource?.private);

    let defaultExpanded =
      showMembersInitially || !accessCanBeManaged
        ? 'view-members'
        : 'invite-members';

    // unrestricted folders will no longer see the invite-members section
    if (isFolderAsset && !isRestrictedFolder) {
      defaultExpanded = 'view-members';
    }

    const [currentItem, setCurrentItem] = useState(
      defaultExpanded === 'invite-members' ? 'InviteMembers' : 'ViewMembers'
    );

    useEffect(() => {
      setCurrentItem(
        defaultExpanded === 'invite-members' ? 'InviteMembers' : 'ViewMembers'
      );
    }, [defaultExpanded]);

    const [expandedHasChanged, setExpandedHasChanged] = useState(false);

    const handleExpandedChange = () => {
      setCurrentItem(
        currentItem === 'ViewMembers' ? 'InviteMembers' : 'ViewMembers'
      );
      if (!expandedHasChanged) {
        setTimeout(() => setExpandedHasChanged(true), 400);
      }
    };

    const initialFocusRefForMemberList = useRef<HTMLInputElement>(undefined);

    useImperativeHandle(forwardedRef, () => ({
      prefetch: () => setFetchData(true),
    }));

    usePreloadAccessInfo({
      fetchData,
      resourceId,
    });

    const { licenseType, onChangeLicenseType } = useLicenseSelect({
      enabled: canInviteGuests,
    });

    const [message, setMessage] = useState('');

    const {
      inputMembers,
      inputTokens,
      inputTokenIds,
      resetTokens,
      hasInvalidEmails,
      totalNewMembers,
      hasNewMembers,
      totalExistingGuests,
      totalNewGuests,
      firstExistingGuestToken,
      removeToken,
      addSuggestionsToTokens,
      addInvalidEmails,
      addEmailsToTokens,
    } = useMemberInputToken({
      licenseType,
      resource: {
        projectId: resourceType === 'Project' ? resourceId : undefined,
        workspaceId: resourceType === 'Workspace' ? resourceId : undefined,
        assetId: resourceType === 'FolderAsset' ? resourceId : undefined,
      },
      roleId: currentRoleId ?? '',
    });

    const suggestedMembers = useMemo(
      () => matchedMembers.filter((entry) => !inputTokenIds.has(entry.id)),
      [matchedMembers, inputTokenIds]
    );

    const handleOnEmailsEntered = async (emails: string[]) => {
      const suggestedUsersByEmail =
        createSuggestedUserEmailMap(suggestedMembers);

      const [matchedEmails, unmatchedEmails] = partition(emails, (email) =>
        Boolean(suggestedUsersByEmail[email])
      );

      if (matchedEmails.length > 0) {
        addSuggestionsToTokens(
          matchedEmails.map((email) => suggestedUsersByEmail[email])
        );
      }

      if (!unmatchedEmails.length) {
        return;
      }

      const [validEmails, invalidEmails] = partition(
        unmatchedEmails,
        isValidEmail
      );

      if (validEmails.length > 0) {
        await addEmailsToTokens(validEmails);
      }

      if (invalidEmails.length > 0) {
        addInvalidEmails(invalidEmails);
      }
    };

    const isManagedByAdobe = useAccountManagedByAdobe();

    const {
      handleQueryChange: onSearchQueryChange,
      handleQueryPaste: onSearchQueryPaste,
      query: currentInputQuery,
      resetQuery,
    } = useMemberEmailInputQuery({
      onQueryChange,
      onEmailsEntered: handleOnEmailsEntered,
    });

    const resetInput = () => {
      resetTokens();
      resetQuery();
    };

    const isGuestOverLimit =
      licenseType === 'GUEST' &&
      totalNewGuests > (guestSeatUsage?.availableSeats ?? 0);

    const { isMemberOverLimit, memberSeatCapForLimitMessage } =
      getMemberSeatLimitState({
        isFreeTierPlan,
        isHardLimit: memberSeatUsage?.isHardLimit,
        licenseType,
        planUserMax: subscription?.plan?.userMax,
        seatLimit: memberSeatUsage?.seatLimit,
        seatsUsed: memberSeatUsage?.seatsUsed,
        totalNewMembers,
      });

    const isSubmitDisabled =
      hasInvalidEmails ||
      inputTokenIds.size === 0 ||
      isGuestOverLimit ||
      message.length > INVITE_MESSAGE_MAX_LENGTH ||
      isMemberOverLimit ||
      (isManagedByAdobe && hasNewMembers) ||
      (resourceType === 'FolderAsset' && hasNewMembers);

    const onMemberClick = (evt: SyntheticEvent<HTMLButtonElement>) => {
      const suggestedUserOrGroup = allMembers.find(
        (member) => member.id === evt.currentTarget.name
      );
      if (suggestedUserOrGroup) {
        addSuggestionsToTokens([suggestedUserOrGroup]);
      }
      initialFocusRefForInvite.current?.focus();
      resetQuery();
    };

    const onPendingMemberClick = async (maybeEmail: string) => {
      if (isValidEmail(maybeEmail)) {
        await addEmailsToTokens([maybeEmail]);
      } else {
        addInvalidEmails([maybeEmail]);
      }

      initialFocusRefForInvite.current?.focus();
      resetQuery();
    };
    const addFirstMatch = async () => {
      const firstMatch = suggestedMembers[0];
      if (firstMatch) {
        addSuggestionsToTokens([firstMatch]);
      } else if (isValidEmail(currentInputQuery)) {
        await addEmailsToTokens([currentInputQuery]);
      } else {
        addInvalidEmails([currentInputQuery]);
      }
      resetQuery();
    };

    const onInputBlur = () => {
      if (isValidEmail(currentInputQuery)) {
        addFirstMatch();
      }
    };

    //@todo (V3FRAME-1840): replace these values with actual counts for tracking

    const projectCount = resourceType === 'Project' ? 1 : 0;
    const projectId = useMemo(
      () => (resourceType === 'Project' ? [resourceId] : []),
      [resourceId, resourceType]
    );
    const workspaceCount = resourceType === 'Workspace' ? 1 : 0;
    const workspaceId = useMemo(
      () => (resourceType === 'Workspace' ? [resourceId] : []),
      [resourceId, resourceType]
    );

    const track = useTrackUnsafe();

    const [replaceState] = use(UpdateAppParams);

    const clearDialogOpenUrlParam = () => {
      replaceState({ members_dialog_open: null });
    };

    const handleClose = () => {
      if (['FolderAsset', 'Project', 'Workspace'].includes(resourceType)) {
        track('invite-modal-dismissed', {
          num_projects_drafted: projectCount,
          project_id: projectId,
          num_workspaces_drafted: workspaceCount,
          workspace_id: workspaceId,
          num_users_drafted: inputTokens.length,
          tab_shown: currentItem,
          source: trackingSource,
        });
      }
      setIsShowing(false);
      setExpandedHasChanged(false);
      onRequestChange(false);
      setCurrentItem(
        defaultExpanded === 'invite-members' ? 'InviteMembers' : 'ViewMembers'
      );
      resetInput();
      onChangeLicenseType('MEMBER');
      setMessage('');
      clearDialogOpenUrlParam();
    };

    useEffect(() => {
      const type_error: string[] = [];
      if (hasInvalidEmails) {
        type_error.push('InvalidEmail');
      }
      if (isGuestOverLimit) {
        type_error.push('GuestLimitReached');
      }
      if (isMemberOverLimit) {
        type_error.push('MemberLimitReached');
      }

      if (
        type_error.length &&
        ['Account', 'Workspace', 'Project', 'FolderAsset'].includes(
          resourceType
        )
      ) {
        track('invite-modal-error-shown', {
          num_projects_drafted: projectCount,
          project_id: projectId,
          num_workspaces_drafted: workspaceCount,
          workspace_id: workspaceId,
          source: trackingSource,
          type_error: type_error.toString(),
        });
      }
    }, [
      hasInvalidEmails,
      isGuestOverLimit,
      isMemberOverLimit,
      projectCount,
      projectId,
      resourceId,
      resourceType,
      track,
      trackingSource,
      workspaceCount,
      workspaceId,
    ]);

    // Both restricted Projects and Workspace
    // must display directMemberships count only
    const displayDirectMembershipOnly =
      isRestrictedProject || resourceType === 'Workspace';

    const totalCount = displayDirectMembershipOnly
      ? directMembershipCount
      : uniqueMemberCount;

    const shouldShowMemberAccessForm =
      (isFolderAsset ? isRestrictedFolder : availableRoles) &&
      accessCanBeManaged;

    const pathToTrackingSource = () => {
      const path = window.location.pathname;
      if (path === '/settings/projects') {
        return 'ProjectInviteModalProjectPageAccountSettings';
      }
      if (path === '/settings/workspaces') {
        return 'WorkspaceInviteModalWorkspacePageAccountSettings';
      }
      return trackingSource;
    };

    const accessTrackingSource = pathToTrackingSource();
    return (
      (accessCanBeManaged || canViewMembers) &&
      shouldRender && (
        <>
          <ExistingGuestDialog
            avatarBackground={
              firstExistingGuestToken?.avatarBackground as
                | ThemeColorsBackground
                | ThemeColorsMeta
            }
            avatarSrc={
              typeof firstExistingGuestToken?.avatarSrc === 'string'
                ? firstExistingGuestToken.avatarSrc
                : undefined
            }
            isShowing={isExistingGuestDialogShowing}
            name={firstExistingGuestToken?.label}
            numGuests={
              totalExistingGuests > 1 ? totalExistingGuests : undefined
            }
            onInviteAsMember={() => {
              handleCreateMemberships({
                members: inputMembers,
                roleId: currentRoleId,
                licenseType: 'MEMBER',
                message,
                notify: true,
              });
              resetInput();
              onChangeLicenseType('MEMBER');
              setMessage('');
              setIsExistingGuestDialogShowing(false);
            }}
            onMoveGuests={() => {
              handleCreateMemberships({
                members: inputMembers,
                roleId: currentRoleId,
                licenseType: 'GUEST',
                notify: false,
              });
              resetInput();
              onChangeLicenseType('MEMBER');
              setMessage('');
              setIsExistingGuestDialogShowing(false);
            }}
            onRequestClose={() => setIsExistingGuestDialogShowing(false)}
            planPeriod={planPeriod}
            subscriptionCost={subscriptionCost}
            zIndex={theme.zIndices.modal + 1}
          />
          <AccordionDialog
            defaultExpanded={defaultExpanded}
            initialFocusRef={
              defaultExpanded === 'invite-members'
                ? initialFocusRefForInvite
                : initialFocusRefForMemberList
            }
            isShowing={Boolean(isShowing)}
            onExpandedChange={handleExpandedChange}
            onRequestClose={handleClose}
          >
            {shouldShowMemberAccessForm && (
              <AccordionDialogItem
                key={`invite${resourceId}`}
                adornmentAfter={TEXT.labelInviteButton}
                adornmentBefore={
                  <ResourceAvatar
                    resourceId={resourceId}
                    resourceImage={resourceImage}
                    resourceName={resourceName}
                    resourcePrivacy={resourcePrivacy}
                    resourceType={resourceType}
                  />
                }
                data-testid="invite-members-accordion-dialog-item"
                id="invite-members"
                title={TEXT.labelInvite(resourceName, resourceType)}
              >
                <MemberAccessForm
                  availableRoles={availableRoles}
                  canInviteGuests={canInviteGuests}
                  currentInputQuery={currentInputQuery}
                  currentRoleId={currentRoleId}
                  deferRender={
                    defaultExpanded !== 'invite-members' || expandedHasChanged
                  }
                  guestSeatLimit={guestSeatUsage?.seatLimit}
                  hasInvalidEmails={hasInvalidEmails}
                  hasNewMembers={hasNewMembers}
                  inputRef={initialFocusRefForInvite}
                  inputTokens={inputTokens}
                  isGuestOverLimit={isGuestOverLimit}
                  isLoading={isLoading}
                  isMemberOverLimit={isMemberOverLimit}
                  isOnTrial={isOnTrial}
                  isRestrictedFolder={isRestrictedFolder}
                  isRestrictedProject={isRestrictedProject}
                  isSubmitDisabled={isSubmitDisabled}
                  licenseType={licenseType}
                  memberSeatLimit={memberSeatCapForLimitMessage}
                  message={message}
                  onCancel={handleClose}
                  onChangeLicenseType={onChangeLicenseType}
                  onChangeRole={onChangeRole}
                  onEmailMessageChange={(e) => setMessage(e.target.value)}
                  onEnterKey={addFirstMatch}
                  onInputBlur={onInputBlur}
                  onMemberClick={onMemberClick}
                  onPendingMemberClick={onPendingMemberClick}
                  onRemoveMember={removeToken}
                  onSearchQueryChange={onSearchQueryChange}
                  onSearchQueryPaste={onSearchQueryPaste}
                  onSubmit={() => {
                    if (totalExistingGuests > 0) {
                      setIsExistingGuestDialogShowing(true);
                      return;
                    }

                    if (
                      ['FolderAsset', 'Project', 'Workspace'].includes(
                        resourceType
                      )
                    ) {
                      track('invite-modal-submitted', {
                        ...calculateUserNumbersForTracking(inputTokens),
                        workspace_count: workspaceCount,
                        workspace_id: workspaceId,
                        project_count: projectCount,
                        project_id: projectId,
                        has_message: Boolean(message),
                        license_type: MEMBER_TYPES[licenseType],
                        resource_visibility:
                          isRestrictedProject || isRestrictedFolder
                            ? 'INVITE_ONLY'
                            : 'INTERNAL',
                        role_name: getRoleNameForTracking(
                          availableRoles,
                          currentRoleId
                        ),
                        source: trackingSource,
                        user_ids: inputTokens.map((token) => token.id),
                      });
                    }

                    handleCreateMemberships({
                      members: inputMembers,
                      roleId: currentRoleId,
                      licenseType,
                      message,
                      notify: true,
                    });
                    resetInput();
                    onChangeLicenseType('MEMBER');
                    setMessage('');
                  }}
                  planPeriod={planPeriod}
                  resourceType={resourceType}
                  shouldManageEmailMessage={true}
                  subscriptionCost={
                    hasNewMembers ? subscriptionCost : undefined
                  }
                  suggestedMembers={
                    hideSuggestedMembers ? undefined : suggestedMembers
                  }
                  totalNewUsers={totalNewMembers}
                />
              </AccordionDialogItem>
            )}

            {canViewMembers && (
              <AccordionDialogItem
                key={`members${resourceId}`}
                adornmentAfter={TEXT.labelManage}
                adornmentBefore={(isExpanded) => (
                  <motion.div
                    animate={{ opacity: isExpanded ? 0 : 1 }}
                    style={{ position: isExpanded ? 'absolute' : 'relative' }}
                    transition={{
                      duration: 0.3,
                      ease: theme.transitions.curves.primary,
                    }}
                  >
                    <div className={styles.avatarGroupWrapper}>
                      <AvatarGroup interactive={!isExpanded}>
                        {firstThreeMemberIds?.map((id) => (
                          <MemberAvatar key={id} membershipId={id} />
                        ))}
                      </AvatarGroup>
                    </div>
                  </motion.div>
                )}
                aria-label={TEXT.ariaLabelManageMembers}
                data-testid="view-members-accordion-dialog-item"
                id="view-members"
                title={
                  <MembersTitle isLoading={isLoading} totalCount={totalCount} />
                }
              >
                {(isVisible) => (
                  <InviteAccessMembers
                    ref={initialFocusRefForMemberList}
                    accessCanBeManaged={accessCanBeManaged}
                    animateControls={memberListControls}
                    availableRoles={availableRoles}
                    backgroundColor="modal"
                    deferRender={
                      defaultExpanded !== 'view-members' || expandedHasChanged
                    }
                    fetchData={fetchData || isVisible}
                    focusSearchOnMount={true}
                    isRestrictedProject={isRestrictedProject}
                    onChangeResource={handleChangeResource}
                    onResourceHasMutated={() =>
                      onResourceHasMutated?.(currentResourceId !== resourceId)
                    }
                    refetchQueries={refetchQueries}
                    resourceId={currentResourceId}
                    resourceType={resourceType}
                    scrollHeight={accessCanBeManaged ? '405px' : '505px'}
                    trackingSource={accessTrackingSource}
                  />
                )}
              </AccordionDialogItem>
            )}
          </AccordionDialog>
        </>
      )
    );
  }
);

const MembersTitle = ({
  totalCount,
  isLoading,
}: {
  totalCount: number | null | undefined;
  isLoading: boolean;
}) => {
  return isLoading || typeof totalCount !== 'number' ? (
    <MembersTitleSkeleton height={'12px'} width={'92px'} />
  ) : (
    TEXT.labelMembers(totalCount)
  );
};

const MembersTitleSkeleton = styled(Skeleton)`
  background: var(--Colors-Border-Secondary, rgba(57, 61, 79, 0.35));
  border-radius: 2px;
`;

const calculateUserNumbersForTracking = (inputTokens: InputToken[]) => {
  const userTypesTally: Record<string, number> = inputTokens.reduce(
    (acc, cur) => {
      // eslint-disable-next-line no-param-reassign
      acc[InputTokenStatus[cur.status]] = acc[InputTokenStatus[cur.status]]
        ? acc[InputTokenStatus[cur.status]] + 1
        : 1;

      if (
        cur.status === InputTokenStatus.GROUP ||
        cur.status === InputTokenStatus.EXISTING_GROUP
      ) {
        // eslint-disable-next-line no-param-reassign
        acc.GROUP_MEMBER_COUNT = cur.memberCount
          ? acc.GROUP_MEMBER_COUNT + cur.memberCount
          : acc.GROUP_MEMBER_COUNT;
      }

      return acc;
    },
    { GROUP_MEMBER_COUNT: 0 }
  );

  const numExistingAccountUsers =
    (userTypesTally.EXISTING_GUEST || 0) +
    (userTypesTally.EXISTING_MEMBER || 0) +
    (userTypesTally.MEMBER || 0) +
    userTypesTally.GROUP_MEMBER_COUNT;
  const numGroups =
    (userTypesTally.GROUP || 0) + (userTypesTally.EXISTING_GROUP || 0);
  const numNewAccountUsers =
    (userTypesTally.NEW_GUEST || 0) + (userTypesTally.NEW_MEMBER || 0);
  const numUsersTotal = numExistingAccountUsers + numNewAccountUsers;

  return {
    num_existing_account_users: numExistingAccountUsers,
    num_groups: numGroups,
    num_new_account_users: numNewAccountUsers,
    num_users_total: numUsersTotal,
  };
};

const createSuggestedUserEmailMap = (
  members: Array<
    SuggestedAccountUser | SuggestedAccountUserGroup | SuggestedShareList
  >
): Record<string, SuggestedAccountUser> => {
  return members.reduce(
    (acc, member) => {
      if (member.__typename === 'SuggestedAccountUser') {
        const email = member.email || member.inviteEmailAddress;
        if (email) {
          return {
            ...acc,
            [email.toLowerCase()]: member,
          };
        }
      }
      return acc;
    },
    {} as Record<string, SuggestedAccountUser>
  );
};
